import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Result,
  Select,
  Space,
  Tag,
  Typography,
  message
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import MoneyInput from "../shared/MoneyInput";
import Amount from "../shared/Amount";
import { formatCents, formatDate } from "../shared/money";
import { isValidIban } from "../settings/constants";
import * as api from "../receivables/creditsApi";

// Dialogs around a customer credit (Kundenguthaben, CB-1..3): offset against an open invoice,
// refund (SEPA / bank / cash ...) and the Insolvenz of a customer (FA-3). Used by the
// customer account and the "Credits" view of the open-items list. On phones antd modals are
// bottom sheets with the footer pinned (design-system/responsive.css).

const today = () => dayjs().format("YYYY-MM-DD");
const iso = d => (d ? dayjs(d).format("YYYY-MM-DD") : today());

export const CREDIT_SOURCES = {
  credit_note: "Correction invoice",
  cancellation: "Cancellation invoice",
  overpayment: "Overpayment",
  manual: "Manual credit"
};

export const CREDIT_STATUS = {
  open: { label: "Open", color: "green" },
  refund_pending: { label: "Refund pending", color: "gold" },
  used: { label: "Used", color: "default" },
  void: { label: "Void", color: "default" }
};

export const CreditSource = ({ credit }) => {
  const { t } = useTranslation("accounting");
  return <span className="acct-wrap">{t(CREDIT_SOURCES[credit?.source?.kind] || "Credit")}</span>;
};

export const CreditStatusTag = ({ status }) => {
  const { t } = useTranslation("accounting");
  const s = CREDIT_STATUS[status] || { label: status, color: "default" };
  return <Tag color={s.color}>{t(s.label)}</Tag>;
};

/** True when a credit still has money that can be offset or refunded. */
export const isUsable = credit =>
  Number(credit?.remaining || 0) > 0 && ["open", "refund_pending"].includes(credit?.status);

// ---------------------------------------------------------------------------------------------
// Verrechnung (CB-3)
// ---------------------------------------------------------------------------------------------

/**
 * invoices: the customer's open invoices as [{ _id, invoiceNumber, open }] (open in cents).
 */
export const OffsetModal = ({ open, credit, invoices = [], onClose, onDone }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const invoiceId = Form.useWatch("invoice", form);
  const candidates = invoices.filter(i => Number(i.open) > 0);
  const chosen = candidates.find(i => String(i._id) === String(invoiceId));
  const max = Math.min(Number(credit?.remaining || 0), Number(chosen?.open ?? Infinity));

  useEffect(() => {
    if (!open) return;
    const first = candidates.length === 1 ? candidates[0] : null;
    form.setFieldsValue({
      invoice: first?._id,
      amount: first ? Math.min(credit?.remaining || 0, first.open) : credit?.remaining,
      date: dayjs()
    });
  }, [open, credit]);

  const onInvoice = id => {
    const inv = candidates.find(i => String(i._id) === String(id));
    if (inv) form.setFieldValue("amount", Math.min(credit?.remaining || 0, inv.open));
  };

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      await api.offsetCredit(credit._id, {
        invoice: values.invoice,
        amount: values.amount,
        date: iso(values.date)
      });
      message.success(t("Credit offset against the invoice"));
      onDone?.();
      onClose();
    } catch (error) {
      message.error(api.errorText(error, t("Could not offset the credit")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t("Offset credit against an invoice")}
      rootClassName="acct-module"
      width={560}
      destroyOnClose
      okText={t("Offset")}
      cancelText={t("Cancel")}
      okButtonProps={{ disabled: !candidates.length }}
      confirmLoading={saving}
      onOk={submit}
    >
      <Typography.Paragraph type="secondary">
        {t("Available credit")}: <Amount cents={credit?.remaining} strong />
      </Typography.Paragraph>
      {candidates.length ? (
        <Form form={form} layout="vertical">
          <Form.Item
            name="invoice"
            label={t("Invoice")}
            rules={[{ required: true, message: t("Please choose an invoice") }]}
          >
            <Select
              onChange={onInvoice}
              options={candidates.map(i => ({
                value: i._id,
                label: `${i.invoiceNumber} · ${t("open")} ${formatCents(i.open)}`
              }))}
            />
          </Form.Item>
          <Form.Item
            name="amount"
            label={t("Amount")}
            rules={[
              { required: true, message: t("Please enter an amount") },
              {
                validator: (_, v) =>
                  v > 0 && v <= max
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(t("At most {{amount}}", { amount: formatCents(max) }))
                      )
              }
            ]}
          >
            <MoneyInput min={0} />
          </Form.Item>
          <Form.Item name="date" label={t("Date")}>
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" allowClear={false} />
          </Form.Item>
          <Typography.Text type="secondary">
            {t(
              "The offset settles the receivable like a payment; revenue and VAT of the invoice stay unchanged."
            )}
          </Typography.Text>
        </Form>
      ) : (
        <Alert type="info" showIcon message={t("This customer has no open invoice to offset.")} />
      )}
    </Modal>
  );
};

// ---------------------------------------------------------------------------------------------
// Rückzahlung (CB-2)
// ---------------------------------------------------------------------------------------------

const REFUND_METHODS = [
  { value: "sepa", label: "SEPA credit transfer (file for online banking)" },
  { value: "bank", label: "Bank transfer (already made)" },
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "other", label: "Other" }
];

/**
 * customerId: the credit's customer (credit.customer may be populated or a bare id).
 * iban: the refund IBAN known for the customer, shown as a hint.
 */
export const RefundModal = ({ open, credit, customerId, iban, onClose, onDone }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [ibanForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [needIban, setNeedIban] = useState(false);
  const [noBank, setNoBank] = useState(false);
  const [batch, setBatch] = useState(null);
  const method = Form.useWatch("method", form);
  const knownIban =
    iban || credit?.customer?.refundIban || credit?.customer?.sepaMandate?.iban || "";

  useEffect(() => {
    if (!open) return;
    setNeedIban(false);
    setNoBank(false);
    setBatch(null);
    form.setFieldsValue({ method: "sepa", amount: credit?.remaining, date: dayjs() });
    ibanForm.resetFields();
    api
      .listBankAccounts()
      .then(res => {
        const list = (res.data.accounts || []).filter(a => a.active !== false);
        setAccounts(list);
        if (list[0]) form.setFieldValue("bankAccount", list[0]._id);
      })
      .catch(() => setAccounts([]));
  }, [open, credit]);

  const refund = async values => {
    setSaving(true);
    try {
      const res = await api.refundCredit(credit._id, {
        method: values.method,
        amount: values.amount,
        date: iso(values.date),
        bankAccount: values.method === "cash" ? undefined : values.bankAccount
      });
      onDone?.();
      if (res.data.batch) {
        setBatch(res.data.batch);
        setNeedIban(false);
      } else {
        message.success(t("Refund recorded"));
        onClose();
      }
    } catch (error) {
      const code = api.errorCode(error);
      if (code === "NO_IBAN") {
        setNeedIban(true);
        onDone?.(); // the credit is now refund_pending
      } else if (code === "NO_BANK_ACCOUNT") setNoBank(true);
      else message.error(api.errorText(error, t("Could not refund the credit")));
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (needIban) {
      let ibanValues;
      try {
        ibanValues = await ibanForm.validateFields();
      } catch {
        return;
      }
      setSaving(true);
      try {
        await api.updateReceivablesData(customerId, {
          refundIban: ibanValues.refundIban,
          refundBic: ibanValues.refundBic || ""
        });
      } catch (error) {
        setSaving(false);
        message.error(api.errorText(error, t("Could not save the IBAN")));
        return;
      }
    }
    await refund(values);
  };

  const download = () =>
    api.downloadRefundXml(batch).catch(error => message.error(api.errorText(error)));

  const needsBank = method && method !== "cash";
  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t("Refund credit")}
      rootClassName="acct-module"
      width={560}
      destroyOnClose
      okText={needIban ? t("Save IBAN and retry") : t("Refund")}
      cancelText={t("Cancel")}
      confirmLoading={saving}
      onOk={submit}
      footer={
        batch
          ? [
              <Button key="close" onClick={onClose}>
                {t("Close")}
              </Button>,
              <Button key="xml" type="primary" onClick={download}>
                {t("Download SEPA file")}
              </Button>
            ]
          : undefined
      }
    >
      {batch ? (
        <Result
          status="success"
          title={t("SEPA transfer created")}
          subTitle={t(
            "Upload the file to your online banking. The refund is booked when the bank statement line arrives and is matched."
          )}
        />
      ) : null}
      <div hidden={Boolean(batch)}>
        <Typography.Paragraph type="secondary">
          {t("Available credit")}: <Amount cents={credit?.remaining} strong />
        </Typography.Paragraph>
        <Form form={form} layout="vertical">
          <Form.Item name="method" label={t("Method")} rules={[{ required: true }]}>
            <Select
              onChange={() => {
                setNeedIban(false);
                setNoBank(false);
              }}
              options={REFUND_METHODS.map(m => ({ value: m.value, label: t(m.label) }))}
            />
          </Form.Item>
          {method === "sepa" && knownIban && !needIban ? (
            <Typography.Paragraph type="secondary" className="acct-wrap">
              {t("Refund to IBAN")}: <Typography.Text code>{knownIban}</Typography.Text>
            </Typography.Paragraph>
          ) : null}
          <Form.Item
            name="amount"
            label={t("Amount")}
            rules={[
              { required: true, message: t("Please enter an amount") },
              {
                validator: (_, v) =>
                  v > 0 && v <= Number(credit?.remaining || 0)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          t("At most {{amount}}", { amount: formatCents(credit?.remaining) })
                        )
                      )
              }
            ]}
          >
            <MoneyInput min={0} />
          </Form.Item>
          <Form.Item
            name="date"
            label={method === "sepa" ? t("Execution date") : t("Date")}
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" allowClear={false} />
          </Form.Item>
          {needsBank ? (
            <Form.Item
              name="bankAccount"
              label={t("Bank account")}
              extra={
                method === "sepa"
                  ? t("The transfer is made from this account.")
                  : t("The refund is booked on this account.")
              }
            >
              <Select
                allowClear
                options={accounts.map(a => ({
                  value: a._id,
                  label: `${a.name}${a.iban ? ` · ${a.iban}` : ""}`
                }))}
              />
            </Form.Item>
          ) : null}
        </Form>
        {noBank ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message={t("Set up a bank account with an IBAN first")}
            description={<a href="/user/accounting/bank">{t("Open bank accounts")}</a>}
          />
        ) : null}
        {needIban ? (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={t("The customer has no valid IBAN")}
              description={t(
                "The refund is marked as pending. Enter the customer's IBAN to create the SEPA transfer now."
              )}
            />
            <Form form={ibanForm} layout="vertical">
              <Form.Item
                name="refundIban"
                label={t("Customer IBAN for refunds")}
                normalize={v =>
                  String(v || "")
                    .replace(/\s+/g, "")
                    .toUpperCase()
                }
                rules={[
                  { required: true, message: t("Please enter the IBAN") },
                  {
                    validator: (_, v) =>
                      isValidIban(v)
                        ? Promise.resolve()
                        : Promise.reject(new Error(t("Please enter a valid IBAN")))
                  }
                ]}
              >
                <Input placeholder="DE00 0000 0000 0000 0000 00" autoComplete="off" />
              </Form.Item>
              <Form.Item name="refundBic" label={t("BIC (optional)")}>
                <Input autoComplete="off" />
              </Form.Item>
            </Form>
          </>
        ) : null}
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------------------------------------
// Insolvenz (FA-3)
// ---------------------------------------------------------------------------------------------

export const InsolvencyModal = ({ open, customer, openInvoices = [], onClose, onDone }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const openTotal = openInvoices.reduce((s, i) => s + Number(i.open || 0), 0);

  useEffect(() => {
    if (open) form.setFieldsValue({ date: dayjs(), court: "", fileNo: "" });
  }, [open, form]);

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      const res = await api.markInsolvent(customer._id, {
        date: iso(values.date),
        court: values.court,
        fileNo: values.fileNo
      });
      message.success(
        t("Insolvency recorded, {{count}} invoices written off", {
          count: res.data.writtenOff?.length || 0
        })
      );
      onDone?.();
      onClose();
    } catch (error) {
      message.error(api.errorText(error, t("Could not record the insolvency")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t("Record insolvency")}
      rootClassName="acct-module"
      width={560}
      destroyOnClose
      okText={t("Record insolvency")}
      okButtonProps={{ danger: true }}
      cancelText={t("Cancel")}
      confirmLoading={saving}
      onOk={submit}
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          openInvoices.length
            ? t("{{count}} open invoices ({{amount}}) will be written off", {
                count: openInvoices.length,
                amount: formatCents(openTotal)
              })
            : t("This customer has no open invoices")
        }
        description={t(
          "The VAT is corrected as of the opening date of the proceedings (§ 17 UStG) and dunning stops. Later payments from the insolvency estate revive the written-off amount."
        )}
      />
      {openInvoices.length ? (
        <Space wrap size={[4, 4]} style={{ marginBottom: 16 }}>
          {openInvoices.map(i => (
            <Tag key={i._id}>
              {i.invoiceNumber} · {formatCents(i.open)}
            </Tag>
          ))}
        </Space>
      ) : null}
      <Form form={form} layout="vertical">
        <Form.Item
          name="date"
          label={t("Opening date of the proceedings")}
          rules={[{ required: true, message: t("Please choose a date") }]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD.MM.YYYY"
            allowClear={false}
            disabledDate={d => d && d.isAfter(dayjs(), "day")}
          />
        </Form.Item>
        <Form.Item name="court" label={t("Insolvency court")}>
          <Input placeholder={t("e.g. Amtsgericht München")} />
        </Form.Item>
        <Form.Item name="fileNo" label={t("File number")}>
          <Input placeholder="1500 IN 123/26" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

/** Pending SEPA refunds of a credit, each with its file download. */
export const PendingRefunds = ({ credit }) => {
  const { t } = useTranslation("accounting");
  const pending = (credit?.usages || []).filter(u => u.kind === "refund" && u.pending && u.batch);
  if (!pending.length) return null;
  return (
    <Space direction="vertical" size={2}>
      {pending.map((u, i) => (
        <Typography.Link
          key={u._id || i}
          onClick={() =>
            api
              .downloadRefundXml({ _id: u.batch, messageId: `${formatDate(u.date)}-${i + 1}` })
              .catch(error => message.error(api.errorText(error)))
          }
        >
          {t("SEPA file")} {formatDate(u.date)} · {formatCents(u.amount)}
        </Typography.Link>
      ))}
    </Space>
  );
};
